import React, { useEffect, useMemo, useState } from "react";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";

import { db } from "../../../config/firebase";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useTheme } from "../../../contexts/ThemeContext";

import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import CardListMobile from "../../../templates/mobile/CardListMobile";
import { pode } from "../../../utils/permissoes";
import { SortableHeader, useSortableTable } from "../../../hooks/useSortableTable";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { NIVEIS } from "../../../config/niveis";
import { temNivelMinimo } from "../../../utils/nivelAcesso";


export default function PreventivasPage() {
  const navigate = useNavigate();
  const { usuario: usuarioLogado, carregando: authLoading } = useAuth();
  const { empresaAtual } = useEmpresa();
  const { theme } = useTheme();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [preventivas, setPreventivas] = useState([]);

  // filtros (modo simples do FiltroBar)
  const [campoFiltro, setCampoFiltro] = useState("codigo");
  const [textoFiltro, setTextoFiltro] = useState("");

  // Permissões
  // (no FIT, normalmente ADMIN controla Cadastros; mantemos compatível com a matriz de permissões também)
  const isAdmin = temNivelMinimo(usuarioLogado, NIVEIS.ADMIN);
  const canTabela = pode(usuarioLogado, "cadastros", "preventivas", "tabela") || isAdmin;
  const canCriar = pode(usuarioLogado, "cadastros", "preventivas", "form") || isAdmin;
  const canEditar = pode(usuarioLogado, "cadastros", "preventivas", "altera") || isAdmin;
  const canDuplicar = pode(usuarioLogado, "cadastros", "preventivas", "duplicar") || isAdmin;
  const canExcluir = canEditar; // usamos o mesmo gate do altera

  async function carregar() {
    if (!usuarioLogado?.grupoId) return;
    setLoading(true);
    setErro("");
    try {
      const q = query(
        collection(db, "preventivasModelos"),
        where("grupoId", "==", usuarioLogado.grupoId)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPreventivas(data);
    } catch (e) {
      console.error(e);
      setErro("Falha ao carregar preventivas (modelos).");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!authLoading) carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, usuarioLogado?.grupoId]);

  const filtradas = useMemo(() => {
    const txt = textoFiltro.trim().toLowerCase();
    if (!txt) return preventivas;

    return preventivas.filter((p) => {
      const v = String(p?.[campoFiltro] || "").toLowerCase();
      return v.includes(txt);
    });
  }, [preventivas, campoFiltro, textoFiltro]);

  const { sortedData, requestSort, sortConfig, getSortDirection } = useSortableTable(
    filtradas,
    "codigo",
    "asc"
  );

  async function handleExcluir(id, label) {
    const ok = window.confirm(`Excluir a preventiva modelo "${label}"?`);
    if (!ok) return;

    try {
      await deleteDoc(doc(db, "preventivasModelos", id));
      await carregar();
    } catch (e) {
      console.error(e);
      window.alert("Não foi possível excluir. Verifique regras/permissões do Firestore.");
    }
  }

  function handleDuplicar(p) {
    navigate("/cadastros/preventivas/novo", { state: { duplicar: p } });
  }

  function handleEditar(id) {
    navigate(`/cadastros/preventivas/editar/${id}`);
  }

  const cards = useMemo(() => {
    return sortedData.map((p) => ({
      id: p.id,
      nome: `${p.codigo || "-"} — ${p.nome || "Sem nome"}`,
      subtitulo: `${p.fabricante || "-"} / ${p.modelo || "-"}`,
      detalhes: [
        { icone: "📋", label: "Itens", valor: String(p.itens?.length || 0) },
      ],
    }));
  }, [sortedData]);

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Nome" },
    { value: "fabricante", label: "Fabricante" },
    { value: "modelo", label: "Modelo" },
  ];

  const info = (
    <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
      Aqui você cadastra <b>modelos</b> de preventivas (por tipo de equipamento). Para gerar a preventiva para um
      equipamento específico, vincule no <b>Cadastro de Equipamentos</b>.
      {empresaAtual?.nome ? (
        <>
          {" "}
          (Empresa atual: <b>{empresaAtual.nome}</b>)
        </>
      ) : null}
    </div>
  );

  const contentDesktop = (
    <div className="pc-table-wrapper">
      <table className="pc-table">
        <thead>
          <tr>
            <SortableHeader
              label="Código"
              sortKey="codigo"
              sortDirection={getSortDirection("codigo")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Preventiva (modelo)"
              sortKey="nome"
              sortDirection={getSortDirection("nome")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Fabricante"
              sortKey="fabricante"
              sortDirection={getSortDirection("fabricante")}
              onSort={requestSort}
            />
            <SortableHeader
              label="Modelo"
              sortKey="modelo"
              sortDirection={getSortDirection("modelo")}
              onSort={requestSort}
            />
            <th>Itens</th>
            <th style={{ width: 160 }}>Ações</th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((p) => (
            <tr key={p.id}>
              <td>{p.codigo || "-"}</td>
              <td>{p.nome || "-"}</td>
              <td>{p.fabricante || "-"}</td>
              <td>{p.modelo || "-"}</td>
              <td>{p.itens?.length || 0}</td>
              <td style={{ display: "flex", gap: 8 }}>
                {canEditar && (
                  <button className="pc-icon-btn" title="Editar" onClick={() => handleEditar(p.id)}>
                    ✏️
                  </button>
                )}
                {canDuplicar && (
                  <button className="pc-icon-btn" title="Duplicar" onClick={() => handleDuplicar(p)}>
                    📄
                  </button>
                )}
                {canExcluir && (
                  <button
                    className="pc-icon-btn"
                    title="Excluir"
                    onClick={() => handleExcluir(p.id, `${p.codigo || ""} ${p.nome || ""}`.trim())}
                  >
                    🗑️
                  </button>
                )}
              </td>
            </tr>
          ))}

          {sortedData.length === 0 && (
            <tr>
              <td colSpan={6} style={{ padding: 14, opacity: 0.75 }}>
                Nenhuma preventiva modelo encontrada.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const actionsDesktop = (
    <div style={{ display: "flex", gap: 10 }}>
      {canCriar && (
        <button className="pc-btn pc-btn-primary" onClick={() => navigate("/cadastros/preventivas/novo")}>
          + Nova Preventiva (Modelo)
        </button>
      )}
      <button className="pc-btn" onClick={carregar}>
        Atualizar
      </button>
    </div>
  );

  if (!canTabela && !authLoading) {
    return (
      <LayoutDesktop
        title="Preventivas (Modelos)"
        subtitle="Você não tem permissão para acessar esta tela."
      >
        <div style={{ padding: 16, opacity: 0.85 }}>Acesso negado.</div>
      </LayoutDesktop>
    );
  }

  // Loading global (auth) ou carregando coleção
  if (authLoading || loading) {
    const Layout = isMobile ? LayoutMobile : LayoutDesktop;
    return (
      <Layout title="Preventivas (Modelos)" subtitle="Cadastro por tipo/modelo de equipamento">
        <div style={{ padding: 16, opacity: theme === "dark" ? 0.8 : 0.9 }}>Carregando...</div>
      </Layout>
    );
  }

  return isMobile ? (
    <LayoutMobile title="Preventivas (Modelos)" subtitle="Cadastro por tipo/modelo de equipamento">
      <FiltroBar
        campos={camposFiltro}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        onAdicionarClick={canCriar ? () => navigate("/cadastros/preventivas/novo") : undefined}
        mostrarAdicionar={!!canCriar}
        addLabel="+ Nova Preventiva"
        placeholder="Filtrar..."
      />
      {info}

      {erro && (
        <div className="pc-alert pc-alert-danger" style={{ marginTop: 12 }}>
          {erro}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <CardListMobile
          items={cards}
          onEditar={canEditar ? (id) => handleEditar(id) : undefined}
          onExcluir={canExcluir ? (id, nomeCard) => handleExcluir(id, nomeCard) : undefined}
          onDuplicar={
            canDuplicar
              ? (id) => {
                  const p = sortedData.find((x) => x.id === id);
                  if (p) handleDuplicar(p);
                }
              : undefined
          }
          onRecarregar={carregar}
          allowEditar={!!canEditar}
          allowExcluir={!!canExcluir}
          allowDuplicar={!!canDuplicar}
        />
      </div>
    </LayoutMobile>
  ) : (
    <LayoutDesktop
      title="Preventivas (Modelos)"
      subtitle="Cadastro por tipo/modelo de equipamento"
      actions={actionsDesktop}
    >
      <FiltroBar
        campos={camposFiltro}
        campoSelecionado={campoFiltro}
        onCampoChange={setCampoFiltro}
        textoFiltro={textoFiltro}
        onTextoChange={setTextoFiltro}
        onAdicionarClick={canCriar ? () => navigate("/cadastros/preventivas/novo") : undefined}
        mostrarAdicionar={!!canCriar}
        addLabel="+ Nova Preventiva"
        placeholder="Filtrar..."
      />
      {info}

      {erro && (
        <div className="pc-alert pc-alert-danger" style={{ marginTop: 12 }}>
          {erro}
        </div>
      )}

      <div style={{ marginTop: 12 }}>{contentDesktop}</div>
    </LayoutDesktop>
  );
}
